import { round } from 'lodash';
import { ChartOptions } from 'chart.js';

import { getOptionLabel, OptionValue } from '../../framework/service/options';
import { CostReport } from '../../../common/application/model/report/CostReport';
import { CustomerReportCostRow } from '../../../common/application/model/report/CustomerReportCostRow';
import { CustomerReportVehicleTypeRow } from '../../../common/application/model/report/CustomerReportVehicleTypeRow';
import { CustomerReport } from '../../../common/application/model/report/CustomerReport';
import { VehicleType } from '../../../common/application/model/enums/VehicleType';
import { formatCurrency, formatNumber } from './helper_service';
import { dateToDbMonthDateString } from '../../../common/framework/util/convert';

export function mapCostReportRows(
    costRows: CustomerReportCostRow[],
    months: string[],
    invoiceRowTypeOptions: OptionValue[],
): CostReport[] {
    let tableRows: CostReport[] = [];
    const invoiceRowTypes = Array.from(new Set(costRows.map((r) => r.invoiceRowType.toString()))).sort((a, b) =>
        a.localeCompare(b),
    );

    for (const month of months) {
        for (const invoiceRowType of invoiceRowTypes) {
            tableRows.push({
                month,
                invoiceRowType: invoiceRowType.toLowerCase(),
                invoiceRowTypeLabel: getOptionLabel(invoiceRowType.toString().toLowerCase(), invoiceRowTypeOptions),
                invoicedExcludingVat: 0,
                invoicedVat: 0,
                invoicedIncludingVat: 0,
                paidVat: 0,
                paidIncludingVat: 0,
                paidExcludingVat: 0,
                projectedExcludingVat: 0,
                projectedVat: 0,
                projectedIncludingVat: 0,
            });
        }
    }

    for (const reportRow of costRows) {
        const selectedRows = tableRows.filter(
            (r) => r.month === reportRow.month && r.invoiceRowType === reportRow.invoiceRowType,
        );

        if (selectedRows.length === 0) {
            continue;
        }
        const row = selectedRows[0];
        row.invoicedExcludingVat = round(row.invoicedExcludingVat + reportRow.invoiced, 2);
        row.invoicedVat = round(row.invoicedVat + reportRow.invoicedVat, 2);
        row.invoicedIncludingVat = round(row.invoicedIncludingVat + (reportRow.invoiced + reportRow.invoicedVat), 2);
        row.projectedExcludingVat = round(row.projectedExcludingVat + reportRow.projected, 2);
        row.projectedVat = round(row.projectedVat + reportRow.projectedVat, 2);
        row.projectedIncludingVat = round(
            row.projectedIncludingVat + (reportRow.projected + reportRow.projectedVat),
            2,
        );
        row.paidExcludingVat = round(row.paidExcludingVat + reportRow.paid, 2);
        row.paidVat = round(row.paidVat + reportRow.paidVat, 2);
        row.paidIncludingVat = round(row.paidIncludingVat + (reportRow.paid + reportRow.paidVat), 2);
    }

    tableRows = tableRows.filter((r) => r.invoicedExcludingVat > 0 || r.projectedExcludingVat > 0);

    return tableRows;
}

export function getTitleOptions(text: string) {
    return {
        display: true,
        text,
        fontSize: 24,
        fontFamily: 'sans-serif',
    };
}

export function getMonthLabels(component: any): string[] {
    return [
        `${component.$t('enum.month.january')}`,
        `${component.$t('enum.month.february')}`,
        `${component.$t('enum.month.march')}`,
        `${component.$t('enum.month.april')}`,
        `${component.$t('enum.month.may')}`,
        `${component.$t('enum.month.june')}`,
        `${component.$t('enum.month.july')}`,
        `${component.$t('enum.month.august')}`,
        `${component.$t('enum.month.september')}`,
        `${component.$t('enum.month.october')}`,
        `${component.$t('enum.month.november')}`,
        `${component.$t('enum.month.december')}`,
    ];
}

export const getMonthLabelsNew = (component: any, months: string[]): string[] => {
    const monthTranslations: string[] = [];

    months.forEach((month) => {
        const splitMonth = month.split('-');
        monthTranslations.push(`${component.$t('enum.monthNumber.' + splitMonth[1])} / ${splitMonth[0]}`);
    });

    return monthTranslations;
};

export function setVehicleTypeRows(
    customerFleetReport: CustomerReport,
    component: any,
): CustomerReportVehicleTypeRow[] {
    const vehicleTypeRows: CustomerReportVehicleTypeRow[] = [];

    if (!customerFleetReport.vehicleTypeRows || !customerFleetReport.vehicleTypeRows.length) {
        return vehicleTypeRows;
    }

    for (const reportRow of customerFleetReport.vehicleTypeRows) {
        if (reportRow.active > 0 || reportRow.acquisition > 0 || reportRow.ending > 0) {
            vehicleTypeRows.push({
                vehicle_type: component.$t(
                    `enum.vehicleType.${reportRow.vehicle_type.toLowerCase().toString()}`,
                ) as VehicleType,
                acquisition: reportRow.acquisition,
                active: reportRow.active,
                ending: reportRow.ending,
                realization: reportRow.realization,
                inactive: reportRow.inactive,
            });
        }
    }

    return vehicleTypeRows;
}

export const chartBackgroundColors: string[] = [
    '#021f87',
    '#2b7aff',
    '#84c317',
    '#00940b',
    '#ff6500',
    '#de1f27',
    '#cb55a6',
    '#6200a6',
    '#ce7d78',
    '#ea9e70',
    '#a48a9e',
    '#c6e1e8',
    '#648177',
    '#0d5ac1',
    '#f205e6',
    '#1c0365',
    '#14a9ad',
    '#4ca2f9',
    '#a4e43f',
    '#d298e2',
    '#6119d0',
    '#d2737d',
    '#c0a43c',
    '#f2510e',
    '#651be6',
    '#79806e',
    '#61da5e',
    '#cd2f00',
    '#9348af',
    '#01ac53',
    '#c5a4fb',
    '#996635',
    '#b11573',
    '#4bb473',
    '#75d89e',
    '#2f3f94',
    '#2f7b99',
    '#da967d',
    '#34891f',
    '#b0d87b',
    '#ca4751',
    '#7e50a8',
    '#c4d647',
    '#e0eeb8',
    '#11dec1',
    '#289812',
    '#566ca0',
    '#ffdbe1',
    '#2f1179',
    '#935b6d',
    '#916988',
    '#513d98',
    '#aead3a',
    '#9e6d71',
    '#4b5bdc',
    '#0cd36d',
    '#250662',
    '#cb5bea',
    '#228916',
    '#ac3e1b',
    '#df514a',
    '#539397',
    '#880977',
    '#f697c1',
    '#ba96ce',
    '#679c9d',
    '#c6c42c',
    '#5d2c52',
    '#48b41b',
    '#e1cf3b',
    '#5be4f0',
    '#57c4d8',
    '#a4d17a',
    '#225b80',
    '#be608b',
    '#96b00c',
    '#088baf',
    '#f158bf',
    '#e145ba',
    '#ee91e3',
    '#05d371',
    '#5426e0',
    '#4834d0',
    '#802234',
    '#6749e8',
    '#0971f0',
    '#8fb413',
    '#b2b4f0',
    '#c3c89d',
    '#c9a941',
    '#41d158',
    '#fb21a3',
    '#51aed9',
    '#5bb32d',
    '#807fb0',
    '#21538e',
    '#89d534',
    '#d36647',
    '#7fb411',
    '#0023b8',
    '#3b8c2a',
    '#986b53',
    '#f50422',
    '#983f7a',
    '#ea24a3',
    '#79352c',
    '#521250',
    '#c79ed2',
    '#d6dd92',
    '#e33e52',
    '#b2be57',
    '#fa06ec',
    '#1bb699',
    '#6b2e5f',
    '#64820f',
    '#1c2710',
    '#21538e',
    '#89d534',
    '#d36647',
    '#7fb411',
    '#0023b8',
    '#3b8c2a',
    '#986b53',
    '#f50422',
    '#983f7a',
    '#ea24a3',
    '#79352c',
    '#521250',
    '#c79ed2',
    '#d6dd92',
    '#e33e52',
    '#b2be57',
    '#fa06ec',
    '#1bb699',
    '#6b2e5f',
    '#64820f',
    '#1c2710',
    '#9cb64a',
    '#996c48',
    '#9ab9b7',
    '#06e052',
    '#e3a481',
    '#0eb621',
    '#fc458e',
    '#b2db15',
    '#aa226d',
    '#792ed8',
    '#73872a',
    '#520d3a',
    '#cefcb8',
    '#a5b3d9',
    '#7d1d85',
    '#c4fd57',
    '#f1ae16',
    '#8fe22a',
    '#ef6e3c',
    '#243eeb',
    '#1dc180',
    '#dd93fd',
    '#3f8473',
    '#e7dbce',
    '#421f79',
    '#7a3d93',
    '#635f6d',
    '#93f2d7',
    '#9b5c2a',
    '#15b9ee',
    '#0f5997',
    '#409188',
    '#911e20',
    '#1350ce',
    '#10e5b1',
    '#fff4d7',
    '#cb2582',
    '#ce00be',
    '#32d5d6',
    '#172320',
    '#608572',
    '#c79bc2',
    '#00f87c',
    '#77772a',
    '#6995ba',
    '#fc6b57',
    '#f07815',
    '#8fd883',
    '#060e27',
    '#96e591',
    '#21d52e',
    '#d00043',
    '#b47162',
    '#1ec227',
    '#4f0f6f',
    '#1d1d58',
    '#947002',
    '#bde052',
    '#e08c56',
    '#28fcfd',
    '#bb09b0',
    '#36486a',
    '#d02e29',
    '#1ae6db',
    '#3e464c',
    '#a84a8f',
    '#911e7e',
    '#3f16d9',
    '#0f525f',
    '#ac7c0a',
    '#b4c086',
    '#c9d730',
    '#30cc49',
    '#3d6751',
    '#fb4c03',
    '#640fc1',
    '#62c03e',
    '#d3493a',
    '#88aa0b',
    '#406df9',
    '#615af0',
    '#4be470',
    '#2a3434',
    '#4a543f',
    '#79bca0',
    '#a8b8d4',
    '#00efd4',
    '#7ad236',
    '#7260d8',
    '#1deaa7',
    '#06f43a',
    '#823c59',
    '#e3d94c',
    '#dc1c06',
    '#f53b2a',
    '#b46238',
    '#2dfff6',
    '#a82b89',
    '#1a8011',
    '#436a9f',
    '#1a806a',
    '#4cf09d',
    '#c188a2',
    '#67eb4b',
    '#b308d3',
    '#fc7e41',
    '#af3101',
    '#ff0650',
    '#71b1f4',
    '#a2f8a5',
    '#e23dd0',
    '#d3486d',
    '#00f7f9',
    '#474893',
    '#3cec35',
    '#1c65cb',
    '#5d1d0c',
    '#2d7d2a',
    '#ff3420',
    '#5cdd87',
    '#a259a4',
    '#e4ac44',
    '#1bede6',
    '#8798a4',
    '#d7790f',
    '#b2c24f',
    '#de73c2',
    '#d70a9c',
    '#25b670',
    '#88e9b8',
    '#c2b0e2',
    '#86e98f',
    '#ae90e2',
    '#1a806b',
    '#436a9e',
    '#0ec0ff',
    '#f812b3',
    '#b17fc9',
    '#8d6c2f',
    '#d3277a',
    '#2ca1ae',
    '#9685eb',
    '#8a96c6',
    '#dba2e6',
    '#76fc1b',
    '#608fa4',
    '#20f6ba',
    '#07d7f6',
    '#dce77a',
    '#77ecca',
];

export const expenseTypeColors: { [rowType: string]: string } = {
    unspecified: '#000',
    '13100': '#0054f0',
    '14050': '#eb0000',
    '14051': '#ff6a10',
    '14052': '#31aefc',
    '14053': '#b5052c',
    '14054': '#00005e',
    '14055': '#5cdd87',
    '14056': '#8a96c6',
    '14057': '#f812b3',
    '14058': '#1c0365',
    '14059': '#ff6500',
    '14060': '#c6e1e8',
    '14061': '#cb55a6',
    '14100': '#29a4bc',
    '14103': '#ba4040',
    '14104': '#e9e6d7',
    '14105': '#620606',
    '14106': '#D6B7A3',
    '14110': '#567B5D',
    '14120': '#e69138',
    '14210': '#6F6F6F',
    '14211': '#323232',
    '14999': '#D1A8B1',
    '15000': '#665628',
    '15001': '#FFD966',
    '16100': '#98ca02',
    '16101': '#88B501',
    '17100': '#29A4BC',
    '18000': '#060',
    '33000': '#021f87',
    '33001': '#1149C1',
    '80000': '#FC4F34',
    '80002': '#3A4862',
};

export const barChartOptions: ChartOptions<'bar'> = {
    elements: {
        arc: {
            borderWidth: 0,
        },
    },
    responsive: true,
    maintainAspectRatio: false,
    hover: {
        mode: 'nearest',
    },
    animation: {
        duration: 500,
    },
    plugins: {
        tooltip: {
            enabled: true,
            mode: 'nearest',
        },
        legend: {
            display: true,
            position: 'top',
            labels: {
                font: {
                    family: 'sans-serif',
                    size: 16,
                },
                boxWidth: 20,
                padding: 20,
                color: '#344B9F',
            },
        },
    },
};

export const doughnutChartOptions: any = {
    elements: {
        arc: {
            borderWidth: 0,
        },
    },
    responsive: true,
    maintainAspectRatio: false,
    hover: {
        mode: 'nearest',
    },
    animation: {
        duration: 1000,
    },
    plugins: {
        tooltip: {
            enabled: true,
            mode: 'nearest',
        },
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                font: {
                    family: 'sans-serif',
                    size: 16,
                },
                boxWidth: 20,
                padding: 20,
            },
        },
    },
    cutout: '70%',
};

export const numberTooltipCallbacks = {
    label: (context: any) => {
        let label = context.dataset.label || '';

        if (label) {
            label += ': ';
        }

        if (context.parsed.y !== null && context.parsed.y !== undefined) {
            label += formatNumber(context.parsed.y);
        } else if (typeof context.parsed === 'number') {
            label += formatNumber(context.parsed);
        }
        return label;
    },
};

export const currencyTooltipCallbacks = {
    label: (context: any) => {
        let label = context.dataset.label || '';

        if (label) {
            label += ': ';
        }

        if (context.parsed.y !== null && context.parsed.y !== undefined) {
            label += formatCurrency(context.parsed.y);
        } else if (typeof context.parsed === 'number') {
            label += formatCurrency(context.parsed);
        }
        return label;
    },
};

export const fakeCostReportData = (component: any): CostReport[] => {
    const monthString = dateToDbMonthDateString(new Date());

    return [
        {
            month: monthString,
            invoiceRowType: '33000',
            invoiceRowTypeLabel: `${component.$t('enum.invoiceRowType.33000')}`,
            invoicedExcludingVat: 440,
            invoicedVat: 105.6,
            invoicedIncludingVat: 545.6,
            paidVat: 0,
            paidIncludingVat: 0,
            paidExcludingVat: 0,
            projectedExcludingVat: 440,
            projectedVat: 105.6,
            projectedIncludingVat: 545.6,
        },
        {
            month: monthString,
            invoiceRowType: '14100',
            invoiceRowTypeLabel: `${component.$t('enum.invoiceRowType.14100')}`,
            invoicedExcludingVat: 2436,
            invoicedVat: 584.64,
            invoicedIncludingVat: 3020.64,
            paidVat: 0,
            paidIncludingVat: 0,
            paidExcludingVat: 0,
            projectedExcludingVat: 2436,
            projectedVat: 584.64,
            projectedIncludingVat: 3020.64,
        },
        {
            month: monthString,
            invoiceRowType: '14120',
            invoiceRowTypeLabel: `${component.$t('enum.invoiceRowType.14120')}`,
            invoicedExcludingVat: 30.5,
            invoicedVat: 7.32,
            invoicedIncludingVat: 37.82,
            paidVat: 0,
            paidIncludingVat: 0,
            paidExcludingVat: 0,
            projectedExcludingVat: 30.5,
            projectedVat: 7.32,
            projectedIncludingVat: 37.82,
        },
        {
            month: monthString,
            invoiceRowType: '17100',
            invoiceRowTypeLabel: `${component.$t('enum.invoiceRowType.17100')}`,
            invoicedExcludingVat: 194.4,
            invoicedVat: 46.7,
            invoicedIncludingVat: 241.1,
            paidVat: 0,
            paidIncludingVat: 0,
            paidExcludingVat: 0,
            projectedExcludingVat: 194.4,
            projectedVat: 46.7,
            projectedIncludingVat: 241.1,
        },
        {
            month: monthString,
            invoiceRowType: '15000',
            invoiceRowTypeLabel: `${component.$t('enum.invoiceRowType.15000')}`,
            invoicedExcludingVat: 7009,
            invoicedVat: 1682.16,
            invoicedIncludingVat: 8691.16,
            paidVat: 0,
            paidIncludingVat: 0,
            paidExcludingVat: 0,
            projectedExcludingVat: 7009,
            projectedVat: 1682.16,
            projectedIncludingVat: 8691.16,
        },
        {
            month: monthString,
            invoiceRowType: '14210',
            invoiceRowTypeLabel: `${component.$t('enum.invoiceRowType.14210')}`,
            invoicedExcludingVat: 280.65,
            invoicedVat: 67.4,
            invoicedIncludingVat: 348.05,
            paidVat: 0,
            paidIncludingVat: 0,
            paidExcludingVat: 0,
            projectedExcludingVat: 280.65,
            projectedVat: 67.4,
            projectedIncludingVat: 348.05,
        },
        {
            month: monthString,
            invoiceRowType: '14211',
            invoiceRowTypeLabel: `${component.$t('enum.invoiceRowType.14211')}`,
            invoicedExcludingVat: 917,
            invoicedVat: 220.08,
            invoicedIncludingVat: 1137.08,
            paidVat: 0,
            paidIncludingVat: 0,
            paidExcludingVat: 0,
            projectedExcludingVat: 917,
            projectedVat: 220.08,
            projectedIncludingVat: 1137.08,
        },
        {
            month: monthString,
            invoiceRowType: '14060',
            invoiceRowTypeLabel: `${component.$t('enum.invoiceRowType.14060')}`,
            invoicedExcludingVat: 92,
            invoicedVat: 22.08,
            invoicedIncludingVat: 114.08,
            paidVat: 0,
            paidIncludingVat: 0,
            paidExcludingVat: 0,
            projectedExcludingVat: 92,
            projectedVat: 22.08,
            projectedIncludingVat: 114.08,
        },
    ];
};
